import {
  Button,
  ListContainer,
  ListContext,
  PAGE_DEFAULT,
  PAGE_SIZE_UNLIMITED,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  formatBoolean,
  formatCurrency,
  formatDate,
  formatSchoolYear,
  pn,
} from "@compass/core";

import { LkStudentFafsaDisplayFieldId, Permission, PermissionAction, StudentEnrollmentListResource, StudentFafsaListDto, StudentFafsaListFilter, StudentFafsaViewResource } from "generated/openApi";
import { useList, useRouter } from "hooks";
import { studentFafsaPageRoute } from "pages/students/student-fafsas/StudentFafsaPage/StudentFafsaPage";
import { useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { isAuthorized } from "services";
import ApiService from "services/apiService";

export const Fafsas = (props: { initialLoad: boolean; studentId: number; }) => {

  const ROOT_PERMISSION: Permission = Permission.StudentFafsa;
  const navigate = useNavigate();
  const { matchTemplatePath } = useRouter();
  const [resource, setResource] = useState<StudentFafsaViewResource>({});
  const properties = resource.properties || [];
  const displayFields = resource.displayFields || [];
  const amountDisplayField = displayFields.find((x) => x.id === LkStudentFafsaDisplayFieldId.Amount);
  const [listFilter, setListFilter] = useState<StudentFafsaListFilter>({
    fafsaFilter: { studentId: props.studentId },
    page: PAGE_DEFAULT,
    pageSize: PAGE_SIZE_UNLIMITED,
    sortObjects: [{ property: pn<StudentFafsaListDto>("schoolYear"), isDesc: true }],
  });
  const [listItems, setListItems] = useState<StudentFafsaListDto[]>([]);

  const { tableContainerRef, loadList } = useList({
    listFilter: listFilter,
    setListFilter: setListFilter,
    fetchList: (listFilter) => {
      ApiService.studentFafsas.getList(listFilter).then((response) => {
        setListItems(response);
      });
    },
  });

  useEffect(() => {
    if (props.initialLoad) {
      loadList(listFilter);

      ApiService.studentFafsas.getViewResource().then((response) => {
        setResource(response);
      });
    }
  }, [props.initialLoad]);

  return (
    <>
      <ListContainer
        fullHeight
        headerNoPadding
        header={
          <Stack
            direction="row"
            sx={{
              background: "#0d57a1",
              color: "info.main",
              px: 2,
              py: 1.2,
              fontSize: "20px",
              justifyContent: "space-between",
              alignItems: "center",
            }}>
            <Typography variant="inherit">FAFSA</Typography>

            <Stack direction="row" alignItems="center" spacing="sm">
              {isAuthorized(ROOT_PERMISSION, PermissionAction.Create) && (
                <Button
                  variant="text"
                  onClick={() =>
                    navigate({
                      pathname: studentFafsaPageRoute.getPath({ id: 0, crudMode: "edit", screenType: "dialog" }),
                      search: studentFafsaPageRoute.getOptionalParams?.({
                        studentId: props.studentId,
                      }),
                    })
                  }
                  sx={{ minWidth: "auto", color: "info.main" }}
                >
                  + New
                </Button>
              )}
            </Stack>
          </Stack>
        }
        tableContainer={
          <TableContainer ref={tableContainerRef}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>School Year</TableCell>
                  <TableCell>Submitted</TableCell>

                  {amountDisplayField && (
                    <TableCell>Amount</TableCell>
                  )}

                </TableRow>
              </TableHead>
              <TableBody>
                {listItems?.map((listItem) => (
                  <TableRow
                    key={listItem.id}
                    onClick={() =>
                      navigate({
                        pathname: studentFafsaPageRoute.getPath({
                          id: listItem.id,
                          crudMode: "view",
                          screenType: "dialog",
                        }),
                      })
                    }
                  >
                    <TableCell>{formatSchoolYear(listItem.schoolYear)}</TableCell>
                    <TableCell>{formatBoolean(listItem.submitted)}</TableCell>
                    {amountDisplayField && (
                      <TableCell>{formatCurrency(listItem.amount)}</TableCell>)}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        }
      />

      <ListContext.Provider value={{ loadList: (options) => loadList(listFilter, { resetPage: options?.resetPage }) }}>
        {matchTemplatePath(studentFafsaPageRoute.templatePath) && <Outlet />}
      </ListContext.Provider>
    </>
  );
};
